import { useEffect, useState } from "react";
import { POSITION_TYPE_LABEL, POSITION_STATUS_LABEL, fmtEUR } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2 } from "lucide-react";
import { PositionKalkBadges } from "@/components/PositionKalkBadges";
import { DecimalInput } from "@/components/DecimalInput";
import { AutoTextarea } from "@/components/AutoTextarea";
import { PRICE_OPTS, QTY_OPTS, RABATT_OPTS } from "@/lib/decimal";

// Gemeinsame Zellklassen – Kopf, Positionszeilen und „Neue Position“ nutzen exakt dieselben Spalten.
export const TD = "px-3 py-2 align-top";
export const TD_NUM = `${TD} text-right`;
export const TD_STATIC = `${TD_NUM} font-mono leading-8 whitespace-nowrap`;

export const BESCHREIBUNG_MAX = 2000; // wie Backend (text_format.BESCHREIBUNG_MAX_LEN)

// Mehrzeilige Beschreibung: Enter = neue Zeile, gespeichert wird beim Verlassen (Strg/⌘+Enter = speichern).
function DescriptionCommitInput({ value, onCommit, ...rest }) {
  const [text, setText] = useState(value || "");
  useEffect(() => { setText(value || ""); }, [value]);
  const commit = () => { if (text !== (value || "")) onCommit(text); };
  return <AutoTextarea value={text} maxLength={BESCHREIBUNG_MAX} onChange={(e) => setText(e.target.value)} onBlur={commit}
    onKeyDown={(e) => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); e.currentTarget.blur(); } }} {...rest} />;
}

// Text-Eingabe mit lokalem Zustand, speichert bei Blur/Enter.
function TextCommitInput({ value, onCommit, ...rest }) {
  const [text, setText] = useState(value || "");
  useEffect(() => { setText(value || ""); }, [value]);
  const commit = () => { if (text !== (value || "")) onCommit(text); };
  return <Input className="h-8" value={text} onChange={(e) => setText(e.target.value)} onBlur={commit}
    onKeyDown={(e) => e.key === "Enter" && commit()} {...rest} />;
}

function bruttoHint(p, idx) {
  const fixed = p.einzelpreis_brutto != null;
  return (v, dirty) => {
    const showFixed = fixed && !dirty;
    const gross = showFixed ? Number(p.einzelpreis_brutto) : v == null ? null : v * (1 + Number(p.mwst_satz) / 100);
    return (
      <span className="text-[10px] text-slate-400 mt-0.5 whitespace-nowrap" data-testid={`pos-preis-brutto-${idx}`}
        title={showFixed ? "Bruttopreis ist fest (Versand/Kalkulation). Eine Netto-Änderung setzt „Preis manuell“." : "Bruttopreis aus Netto + MwSt."}>
        {gross == null ? "brutto –" : `brutto ${fmtEUR(gross)}`}{showFixed ? " · fix" : ""}
      </span>
    );
  };
}

// Gesamt-Zelle: ohne Rabatt wie bisher; mit Rabatt Originalpreis (durchgestrichen), Rabattbetrag und rabattierter Betrag
function GesamtCell({ p, z, idx }) {
  const gesamtNetto = z ? z.netto : p.einzelpreis_brutto != null
    ? (Number(p.einzelpreis_brutto) * Number(p.menge)) / (1 + Number(p.mwst_satz) / 100)
    : Number(p.einzelpreis_netto) * Number(p.menge);
  return (
    <td className={`${TD_STATIC} text-slate-900`} data-testid={`pos-gesamt-${idx}`}>
      {z?.rabatt_prozent > 0 && <div className="text-[11px] text-slate-400 line-through leading-4" data-testid={`pos-gesamt-original-${idx}`}>{fmtEUR(z.netto_vor_rabatt)}</div>}
      <div className="leading-6">{fmtEUR(gesamtNetto)}</div>
      {z?.rabatt_prozent > 0 && <div className="text-[11px] text-amber-700 leading-4" data-testid={`pos-rabatt-betrag-${idx}`}>−{fmtEUR(z.rabatt_betrag)} brutto</div>}
    </td>
  );
}

export function PositionRow({ p, idx, z, locked, onUpdate, onDelete }) {
  return (
    <tr className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 transition-colors" data-testid={`position-row-${idx}`}>
      <td className={TD}>
        <Select disabled={locked} value={p.typ} onValueChange={(v) => onUpdate(p, { typ: v })}>
          <SelectTrigger className="h-8 min-w-[130px]" data-testid={`pos-typ-${idx}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(POSITION_TYPE_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className={`${TD} min-w-[200px]`}>
        <DescriptionCommitInput disabled={locked} value={p.beschreibung} onCommit={(v) => onUpdate(p, { beschreibung: v })} aria-label="Beschreibung (Enter = neue Zeile)" title="Enter = neue Zeile · gespeichert beim Verlassen des Feldes" data-testid={`pos-beschr-${idx}`} />
        {p.name && <div className="text-[11px] font-semibold text-slate-600 mt-1" data-testid={`pos-name-${idx}`}>{p.name}</div>}
        <PositionKalkBadges p={p} idx={idx} />
        {p.kalk?.preis_modus === "stueck" && Number(p.menge) !== Number(p.kalk.params?.quantity) && (
          <p className="text-[11px] text-amber-700 mt-1" role="note" data-testid={`pos-kalk-menge-hint-${idx}`}>
            Preis wurde für {p.kalk.params?.quantity} Stück kalkuliert – bitte neu kalkulieren
          </p>
        )}
      </td>
      <td className={TD}><TextCommitInput disabled={locked} className="h-8 w-28" value={p.material} onCommit={(v) => onUpdate(p, { material: v })} aria-label="Material" data-testid={`pos-material-${idx}`} /></td>
      <td className={TD}><TextCommitInput disabled={locked} className="h-8 w-24" value={p.farbe} onCommit={(v) => onUpdate(p, { farbe: v })} aria-label="Farbe" data-testid={`pos-farbe-${idx}`} /></td>
      <td className={TD_NUM}><DecimalInput disabled={locked} value={p.menge} opts={QTY_OPTS} className="w-20" aria-label="Menge" testId={`pos-menge-${idx}`} onCommit={(v) => onUpdate(p, { menge: v })} /></td>
      <td className={TD_NUM}><DecimalInput disabled={locked} value={p.einzelpreis_netto} opts={PRICE_OPTS} className="w-28" aria-label="Einzelpreis netto" testId={`pos-preis-${idx}`}
        onCommit={(v) => onUpdate(p, { einzelpreis_netto: v, einzelpreis_brutto: null })} renderHint={bruttoHint(p, idx)} /></td>
      <td className={TD_NUM}><DecimalInput disabled={locked} value={p.rabatt_prozent || 0} opts={RABATT_OPTS} className="w-20" aria-label="Rabatt in Prozent" testId={`pos-rabatt-${idx}`}
        onCommit={(v) => onUpdate(p, { rabatt_prozent: v ?? 0 })} /></td>
      <td className={`${TD_STATIC} text-slate-600`} data-testid={`pos-mwst-${idx}`}>{p.mwst_satz}%</td>
      <GesamtCell p={p} z={z} idx={idx} />
      <td className={TD}>
        <Select value={p.status} onValueChange={(v) => onUpdate(p, { status: v })}>
          <SelectTrigger className="h-8 min-w-[130px]" data-testid={`pos-status-${idx}`}><SelectValue /></SelectTrigger>
          <SelectContent>{Object.entries(POSITION_STATUS_LABEL).map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
        </Select>
      </td>
      <td className={TD}>
        {!locked && <Button size="sm" variant="ghost" className="h-8" onClick={() => onDelete(p)} aria-label="Position löschen" data-testid={`pos-delete-${idx}`}><Trash2 className="w-4 h-4" /></Button>}
      </td>
    </tr>
  );
}
